import { toast } from "react-toastify";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button, Modal, Nav } from "react-bootstrap";
import { useSelector } from "react-redux";
import PulseLoader from "react-spinners/PulseLoader";

import {
  actDatePayload,
  actValidationMessage,
  courtTimeForInput,
  formatCourtDateTime,
} from "@/Utils/litigationDates.mjs";
import { formatCalendarDateForInput } from "@/Utils/calendarDate.mjs";

import {
  useCalculatedActEndDate,
  useLitigationDatePreviews,
} from "../../LitigationDashboard/useLitigationDateCalculations";
import { getClientId, getCaseId, fetchPageObject, addClickRecord } from "../../../Utils/helper";
import SelectStateModal from "../../TreatmentPage/modals/state-modal/SelectStateModal";
import courtIcon from "../../../../public/BP_resources/images/icon/litigation-icon-color.svg";
import incidentIcon from "../../../assets/images/incident.svg";
import "./sOLActPopupCasePage.css";
import AssignNewActCaseWorkersArea from "../../LitigationDashboard/Modals/AssignNewActCaseWorkersArea";
import { api_without_cancellation } from "../../../api/api";
import SOLIcon from "../../../assets/litigation/sol.svg";
import {
  getDateEndpointLabel,
  getDependantEndpoint,
  getUsesEndDate,
  getUsesStartDate,
} from "../../../Utils/litigationDateLayout";

const SOL_WIZARD_STEPS = [
  { id: "eventTemplate", label: "SOL Templates" },
  { id: "parties", label: "Parties" },
  { id: "information", label: "Dates" },
  { id: "location", label: "Location" },
  { id: "workers", label: "Calendar" },
];

const SOLWizardTabs = ({ selectedId, onSelect, hasEvent, showLocation }) => (
  <div className="sol-act-wizard-nav invisible-scrollbar">
    <Nav activeKey={selectedId} onSelect={onSelect} className="sol-act-wizard-tabs">
      {SOL_WIZARD_STEPS.map((step, index) => {
        const disabled =
          (step.id !== "eventTemplate" && !hasEvent) ||
          (step.id === "location" && (!hasEvent || !showLocation));

        return (
          <Nav.Link
            key={step.id}
            eventKey={step.id}
            disabled={disabled}
            className="sol-act-wizard-tab"
            style={{ zIndex: SOL_WIZARD_STEPS.length - index }}
          >
            <span className="text-nowrap">{step.label}</span>
          </Nav.Link>
        );
      })}
    </Nav>
  </div>
);

//for litigation page
const initialState = {
  name: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  phone_number: "",
  fax: "",
  email: "",
  website: "",
  event_id: null,
  for_client: null,
  for_clients: [],
  for_case: null,
  for_defendant: null,
  is_allday: false,
  is_duration: false,
  service_date: null,
  for_service: null,
  calculated_dates: "",
  events_blocked: [],
  events_triggered: [],
  dependant_date_type: null,
  start_date: "",
  end_date: "",
  start_time: "",
  end_time: "",
  sameDay: false,
  meetingurl: "",
  locationType: null,
  trigger_on_accept: [],
  trigger_on_reject: [],
  trigger_on_deadline: [],
  trigger_on_dependant_date: [],
  trigger_type: "",
  server: "",
  served: "",
  is_blocked: "",
  description: "",
};

function SOLActPopupCasePage({
  showPopup,
  handleClose,
  litigationDetail,
  isBlocked = false,
  refetch,
}) {
  const clientId = getClientId();
  const caseId = getCaseId();
  const [serviceData, setServiceData] = useState([]);
  const [eventsData, setEventsData] = useState([]);
  const [calculatedDates, setCalculatedDates] = useState([]);
  const [form, setForm] = useState(initialState);
  const [caseData, setCaseData] = useState({});
  const [selectedContact, setSelectedContact] = useState("");
  const [caseAddresses, setCaseAddresses] = useState([]);
  const { statesData } = useSelector((state) => state.states);
  const [calculatingEndDate, setCalculatingEndDate] = useState(false);
  const [dependantDateFieldLabel, setDependantDateFieldLabel] = useState("Dependant Date");
  const [showLocationTab, setShowLocationTab] = useState(false);
  const [showServiceDateField, setShowServiceDateField] = useState(false);
  const [stateShow, setStateShow] = useState(false);
  const handleStateShow = () => setStateShow(!stateShow);
  const [locationState, setLocationState] = useState("");
  const [partiesData, setPartiesData] = useState();
  const [showToFrom, setShowToFrom] = useState(false);
  const [serviceMethodId, setServiceMethodId] = useState(null);
  const [toLabel, setToLabel] = useState("Serviced");
  const [fromLabel, setFromLabel] = useState("Server");
  const dropdownRef2 = useRef(null);
  const [isBlocking, setIsBlocking] = useState(isBlocked);
  const [fetchingSOLEvents, setFetchingSOLEvents] = useState(isBlocked);

  const caseDataCurrentCase = useSelector((state) => state?.caseData?.current);
  const [firmUsers, setFirmUsers] = useState([]);
  const [otherFirmUser, setOtherFirmUsers] = useState([]);
  const [pageObject, setPageObject] = useState(null);
  const [clientsList, setClientsList] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [defendantsList, setDefendantsList] = useState([]);
  const [selectedDefendantId, setSelectedDefendantId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeTab, setActiveTab] = useState("eventTemplate");
  const page_id_click_record = useSelector(
    (state) => state.page_id_click_record.page_id_click_record
  );
  const showStartDate = getUsesStartDate(selectedEvent);
  const showEndDate = getUsesEndDate(selectedEvent);
  const dependantEndpoint = getDependantEndpoint(selectedEvent);
  const dependantDateValue = form[dependantEndpoint] || "";
  const startDateLabel = getDateEndpointLabel(selectedEvent, "start_date", dependantDateFieldLabel);
  const endDateLabel = getDateEndpointLabel(selectedEvent, "end_date", dependantDateFieldLabel);

  useEffect(() => {
    let isMounted = true;

    const loadPageObject = async () => {
      if (!caseId || pageObject) return;

      try {
        const data = await fetchPageObject(caseId, 19);
        if (isMounted) {
          setPageObject(data);
        }
      } catch (error) {
        console.error("Error fetching SOL page object:", error);
      }
    };

    loadPageObject();

    return () => {
      isMounted = false;
    };
  }, [caseId, pageObject]);

  useEffect(() => {
    const fetchCaseWorkers = async () => {
      try {
        const res = await api_without_cancellation.get(
          "/api/tasks-page/case_workers_and_other_firm_users/",
          {
            params: {
              case_id: caseId,
            },
          }
        );
        const data = await res.data;
        setFirmUsers(
          data.case_role_users.map((worker) => ({
            firm_user: worker,
          }))
        );
        setOtherFirmUsers(
          data.other_firm_users.map((worker) => ({
            firm_user: worker,
          }))
        );
      } catch (error) {
        console.error(error);
      }
    };

    fetchCaseWorkers();
  }, []);
  const _case_users = [
    caseDataCurrentCase?.firm_user1,
    caseDataCurrentCase?.firm_user2,
    caseDataCurrentCase?.firm_user3,
  ];
  const _case_users1 = [
    caseDataCurrentCase?.firm_user4,
    caseDataCurrentCase?.firm_user5,
    caseDataCurrentCase?.firm_user6,
  ];
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [uniqueIdUsers, setUniqueIdUsers] = useState([]);

  const _handleBlocking = () => {
    setIsBlocking(!isBlocking);
    setForm((prevForm) => ({
      ...prevForm,
      name: "[Blocking]",
      address1: "[Blocking]",
      address2: "[Blocking]",
      city: "",
      state: "",
      zip: "",
      phone_number: "",
      fax: "",
      email: "",
      website: "",
      meetingurl: "",
      locationType: null,
      is_blocked: !isBlocking,
    }));
  };

  const shouldDisableSubmit = () => {
    if (isBlocking) {
      // When blocking, only disable if trying to create duplicate (isBlocked is true)
      return isBlocked;
    } else {
      // When not blocking, use standard validation
      return form?.event_id === null;
    }
  };

  const clearLocationFields = () => {
    setForm((prevForm) => ({
      ...prevForm,
      name: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
      phone_number: "",
      fax: "",
      email: "",
      website: "",
      meetingurl: "",
      locationType: null,
    }));
  };

  const fetchPartiesData = async (clientType = null, defendantTypes = null) => {
    try {
      const response = await api_without_cancellation.get(
        `/api/litigation-page/defendants-and-other-parties/${clientId}/${caseId}/`,
        {
          params: {
            litigation_event_popup: true,
            ...(clientType && { client_types: clientType }),
            ...(defendantTypes?.length > 0 && { defendant_types: defendantTypes.join(",") }),
          },
        }
      );
      setPartiesData(combineParties(response.data));
      setClientsList(response.data?.clients || []);
      setDefendantsList(response.data?.defendants || []);
      return response.data?.clients || [];
    } catch (err) {
      console.error("error", err);
      return [];
    }
  };

  const fetchCaseData = async () => {
    try {
      const response = await api_without_cancellation.get(
        `/api/litigation-page/litigation_case/${caseId}/`
      );
      setCaseData(response.data);
    } catch (err) {
      console.error("error", err);
    }
  };

  const fetchServiceData = async () => {
    try {
      const response = await api_without_cancellation.get("/api/litigation-page/service-methods/");
      setServiceData(response.data);
    } catch (err) {
      console.error("error", err);
    }
  };

  const fetchSOLTemplatessData = async () => {
    try {
      setFetchingSOLEvents(true);
      const response = await api_without_cancellation.get(
        "/api/litigation-page/sol-templates-for-popups/",
        {
          params: {
            state: litigationDetail?.state?.id,
            county: litigationDetail?.county?.id,
            state_fed: litigationDetail?.jurisdiction_type?.name || "",
          },
        }
      );
      setEventsData(response.data);
    } catch (err) {
      console.error("error", err);
    } finally {
      setFetchingSOLEvents(false);
    }
  };

  const getCaseAddresses = async () => {
    try {
      const response = await api_without_cancellation.get(
        `/api/litigation-page/get_case_addresses/${clientId}/${caseId}/`
      );
      getContactOptions(response.data.contacts);
    } catch (err) {
      console.error("Error fetching Case Addresses:", err);
    }
  };

  useCalculatedActEndDate({
    enabled: showPopup && showStartDate && showEndDate,
    eventId: form.event_id,
    startDate: form.start_date,
    endDate: form.end_date,
    serviceMethodId,
    sol: true,
    setForm,
    setCalculatingEndDate,
  });
  const calculatedDatePreviews = useLitigationDatePreviews(
    (calculatedDates || []).map((date) => ({
      key: "calculated-date-" + date.id,
      calculatedDateId: date.id,
      startDate: dependantDateValue,
      serviceMethodId,
    })),
    showPopup
  );

  useEffect(() => {
    if (showPopup && !isBlocked && litigationDetail?.id) {
      fetchPartiesData();
      fetchServiceData();
      fetchCaseData();
      getCaseAddresses();
      fetchSOLTemplatessData();
    }
  }, [showPopup, isBlocked, litigationDetail]);

  const getDefendantDisplayName = useCallback((defendant) => {
    const type = defendant?.defendantType?.name || "";
    const name =
      defendant?.entity_name ||
      `${defendant?.first_name || ""} ${defendant?.last_name || ""}`.trim();
    return type ? `${name} - ${type}` : name;
  }, []);

  // Kick back to the template step whenever the active step becomes disabled.
  useEffect(() => {
    if (!form?.event_id && activeTab !== "eventTemplate") {
      setActiveTab("eventTemplate");
    } else if (activeTab === "location" && !showLocationTab) {
      setActiveTab("eventTemplate");
    }
  }, [form?.event_id, showLocationTab, activeTab]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({ ...prevForm, [name]: value }));
  };

  // for event buttons
  const handleEventOptionSelect = (id) => {
    const newValue = form?.event_id === id ? null : id;
    if (newValue !== null) {
      handleEventChange({ target: { name: "event_id", value: newValue } });
    } else {
      setCalculatedDates(null);
      setDependantDateFieldLabel("Dependant Date");
      setShowLocationTab(false);
      setShowServiceDateField(false);
      setShowToFrom(false);
      setSelectedEvent(null);
      setSelectedClientId(null);
      setSelectedDefendantId(null);
      fetchPartiesData();
      setForm((prevForm) => ({
        ...prevForm,
        event_id: null,
        is_allday: false,
        is_duration: false,
        service_date: null,
        for_service: null,
        calculated_dates: "",
        events_blocked: [],
        events_triggered: [],
        dependant_date_type: null,
        start_date: "",
        end_date: "",
        start_time: "",
        end_time: "",
        sameDay: false,
        meetingurl: "",
        trigger_on_accept: [],
        trigger_on_reject: [],
        trigger_on_deadline: [],
        trigger_on_dependant_date: [],
        trigger_type: "",
        server: "",
        served: "",
        description: "",
      }));
      clearLocationFields();
    }
  };

  const handleIntChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: Number(value),
    }));
  };

  const handleCalDatesChange = (e) => {
    const id = parseInt(e.target.value);
    if (e.target.checked) {
      setForm((prevForm) => ({
        ...prevForm,
        calculated_dates: [...prevForm.calculated_dates, id],
      }));
    } else {
      setForm((prevForm) => ({
        ...prevForm,
        calculated_dates: prevForm.calculated_dates.filter((date) => date !== id),
      }));
    }
  };

  const handleEventChange = async (e) => {
    const event = eventsData?.find((event) => event.id === Number(e.target.value)) || null;
    if (event) {
      const eventShowsStartDate = getUsesStartDate(event);
      const eventShowsEndDate = getUsesEndDate(event);
      const eventDependantEndpoint = getDependantEndpoint(event);
      setSelectedEvent(event);
      const clients = await fetchPartiesData(event?.client_types, event?.defendant_types);
      setSelectedDefendantId(null);
      setSelectedClientId(
        clients.some((client) => client.id === Number(clientId)) ? Number(clientId) : null
      );
      if (event?.calculated_dates_id?.length > 0) {
        setCalculatedDates(event?.calculated_dates_id);
        setForm((prevForm) => ({
          ...prevForm,
          calculated_dates: event.calculated_dates_id.map((date) => date.id),
        }));
      } else {
        setCalculatedDates(null);
      }
      setDependantDateFieldLabel(
        event?.dependent_date_type_id?.dependent_date_name || "Dependant Date"
      );
      if (event?.dependent_date_type_id?.dependent_date_name === "Incident Date") {
        // eslint-disable-next-line no-unsafe-optional-chaining
        const dateOnly = formatCalendarDateForInput(caseData?.incident_date);

        setForm((prevForm) => ({
          ...prevForm,
          [eventDependantEndpoint]: dateOnly,
        }));
      }

      if (event?.sameDay) {
        const formattedEndTime = courtTimeForInput(event.sameDay_endTime) || null;
        setForm((prevForm) => ({
          ...prevForm,
          is_allday: false,
          is_duration: false,
          sameDay: true,
          end_time: formattedEndTime,
        }));
      } else if (event?.is_allday) {
        setForm((prevForm) => ({
          ...prevForm,
          is_allday: true,
          is_duration: false,
          sameDay: false,
        }));
      } else if (event?.is_duration) {
        setForm((prevForm) => ({
          ...prevForm,
          is_allday: false,
          is_duration: true,
          sameDay: false,
        }));
      } else {
        setForm((prevForm) => ({
          ...prevForm,
          is_allday: false,
          is_duration: false,
          sameDay: false,
        }));
      }

      if (event?.dependent_date_type_id?.id) {
        setForm((prevForm) => ({
          ...prevForm,
          dependant_date_type: Number(event?.dependent_date_type_id?.id) || null,
        }));
      }
      if (event?.trigger_type === "DependantDate") {
        setForm((prevForm) => ({
          ...prevForm,
          trigger_on_dependant_date: event?.trigger_on_dependant_date || [],
          trigger_on_deadline: event?.trigger_on_deadline || [],
        }));
      } else if (event?.trigger_type === "Accept/Reject") {
        setForm((prevForm) => ({
          ...prevForm,
          trigger_on_accept: event?.trigger_on_accept || [],
          trigger_on_reject: event?.trigger_on_reject || [],
          trigger_on_deadline: event?.trigger_on_deadline || [],
        }));
      }
      if (event?.no_location) {
        setShowLocationTab(false);
        clearLocationFields();
      } else {
        setShowLocationTab(true);
      }
      if (
        event?.dependent_date_type_id?.field_name?.toLowerCase() === "service_date" &&
        event?.service_method
      ) {
        setShowServiceDateField(true);
      } else {
        setShowServiceDateField(false);
      }
      if (event?.to_from) {
        setShowToFrom(true);
        setFromLabel(event?.fromlabel || "Server");
        setToLabel(event?.tolabel || "Serviced");
      } else {
        setShowToFrom(false);
        setToLabel("");
        setFromLabel("");
      }
      setForm((prevForm) => ({
        ...prevForm,
        event_id: Number(event?.id) || null,
        description: event?.event_description || "",
        start_date: eventShowsStartDate ? prevForm.start_date : "",
        end_date: eventShowsEndDate ? prevForm.end_date : "",
        start_time: eventShowsStartDate ? prevForm.start_time : "",
        end_time: eventShowsEndDate ? prevForm.end_time : "",
      }));
    }
  };

  const _handleEventAction = (event, action) => {
    setForm((prevForm) => {
      // Remove from both arrays first
      const newTriggered = prevForm.events_triggered?.filter((id) => id !== event.id) || [];
      const newBlocked = prevForm.events_blocked?.filter((id) => id !== event.id) || [];

      // If clicking the same button that's already active, just remove it
      if (
        (action === "trigger" && prevForm.events_triggered?.includes(event.id)) ||
        (action === "block" && prevForm.events_blocked?.includes(event.id))
      ) {
        return {
          ...prevForm,
          events_triggered: newTriggered,
          events_blocked: newBlocked,
        };
      }

      // Add to appropriate array based on action
      if (action === "trigger") {
        newTriggered.push(event.id);
      } else {
        newBlocked.push(event.id);
      }

      return {
        ...prevForm,
        events_triggered: newTriggered,
        events_blocked: newBlocked,
      };
    });
  };

  const [isOpen2, setIsOpen2] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(
    selectedContact ? caseAddresses.findIndex((opt) => opt.contact === selectedContact) : ""
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef2.current && !dropdownRef2.current.contains(event.target)) {
        setIsOpen2(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDropdownToggle2 = () => {
    setIsOpen2((prev) => !prev);
  };

  const handleAddressChange = (index) => {
    setIsOpen2(false);
    setSelectedIndex(index);
    if (index !== "") {
      const selectedOption = caseAddresses[index];
      setSelectedContact(selectedOption.contact);

      // Get the appropriate contact details based on the selected contact type
      const contactDetails = selectedOption.contact[selectedOption.contact.selectedContact];

      // Update the form with contact details
      if (contactDetails) {
        setForm((prevForm) => ({
          ...prevForm,
          name: contactDetails.name || "",
          address1: contactDetails.address1 || "",
          address2: contactDetails.address2 || "",
          city: contactDetails.city || "",
          state: contactDetails.state || "",
          zip: contactDetails.zip || "",
          phone_number: contactDetails.phone_number || "",
          fax: contactDetails.fax || "",
          email: contactDetails.email || "",
          website: contactDetails.website || "",
        }));
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const updatedForm = Object.entries(form).reduce((acc, [key, value]) => {
        if (value !== null && value !== "" && (!Array.isArray(value) || value.length > 0)) {
          acc[key] = value;
        }

        return acc;
      }, {});
      updatedForm.for_client = selectedClientId || clientId;
      updatedForm.for_clients = [selectedClientId || clientId];
      updatedForm.for_case = caseId;
      if (selectedDefendantId) {
        updatedForm.for_defendant = selectedDefendantId;
      }

      const uniqueWorkerIds = [...new Set(uniqueIdUsers)];
      updatedForm.workers_on_acts = uniqueWorkerIds;

      const response = await api_without_cancellation.post(
        "/api/litigation-page/sol-act/",
        actDatePayload(updatedForm)
      );
      if (response.status === 201) {
        handleClose();
        setForm(initialState);
        setSelectedClientId(null);
        setSelectedDefendantId(null);
        setSelectedEvent(null);
        setActiveTab("eventTemplate");
        refetch();
      }
    } catch (error) {
      console.error(error);
      toast.error(actValidationMessage(error));
    }
  };

  const getContactOptions = (contacts) => {
    const options = [];

    // 1. Court contacts
    contacts.forEach((contact) => {
      if (contact.court_contact) {
        options.push({
          src: courtIcon,
          label: `${contact.type} - ${contact.name} Address`,
          contact: { ...contact, selectedContact: "court_contact" },
        });
      }
    });

    // 2. Opposing Counsels
    contacts.forEach((contact) => {
      if (contact.contact && contact.type === "Opposing Counsel") {
        options.push({
          src: "",
          label: `${contact.type} - ${contact.name} Address`,
          contact: { ...contact, selectedContact: "contact" },
        });
      }
    });

    // 3. Defense Attorneys
    contacts.forEach((contact) => {
      if (contact.contact && contact.type === "Defense Attorney") {
        options.push({
          src: "",
          label: `${contact.type} - ${contact.name} Address`,
          contact: { ...contact, selectedContact: "contact" },
        });
      }
    });

    // 4. Client contacts
    contacts.forEach((contact) => {
      if (contact.contact_1) {
        options.push({
          src: "",
          label: `${contact.type} - ${contact.name} Address 1`,
          contact: { ...contact, selectedContact: "contact_1" },
        });
      }

      if (contact.contact_2) {
        options.push({
          src: "",
          label: `${contact.type} - ${contact.name} Address 2`,
          contact: { ...contact, selectedContact: "contact_2" },
        });
      }
    });

    // 5. Incident contacts
    contacts.forEach((contact) => {
      if (contact.incident_contact) {
        options.push({
          src: incidentIcon,
          label: `${contact.type} - ${contact.name}`,
          contact: { ...contact, selectedContact: "incident_contact" },
        });
      }
    });

    // 6. Defendant contacts
    contacts.forEach((contact) => {
      if (contact.home_contact) {
        options.push({
          src: "",
          label: `${contact.type} - ${contact.name} Home Address`,
          contact: { ...contact, selectedContact: "home_contact" },
        });
      }

      if (contact.work_contact) {
        options.push({
          src: "",
          label: `${contact.type} - ${contact.name} Work Address`,
          contact: { ...contact, selectedContact: "work_contact" },
        });
      }

      // Other party contact fields
      if (contact.party_home_contact) {
        options.push({
          src: "",
          label: `${contact.type} - ${contact.name} Home Address`,
          contact: { ...contact, selectedContact: "party_home_contact" },
        });
      }
    });

    setCaseAddresses(options);
  };

  const handleStateChange = (state) => {
    setForm((prevForm) => ({
      ...prevForm,
      state: state.StateAbr,
    }));
    setLocationState(state.StateAbr);
  };

  // SOL is between a single client and a single defendant — one of each
  const handleClientToggle = (id) => {
    setSelectedClientId((prev) => (prev === id ? null : id));
  };

  const handleDefendantToggle = (id) => {
    setSelectedDefendantId((prev) => (prev === id ? null : id));
    addClickRecord(999, page_id_click_record);
  };

  const combineParties = (partiesData) => {
    const combinedList = [];

    // Add clients
    if (partiesData?.clients) {
      partiesData?.clients?.forEach((client) => {
        combinedList.push({
          id: client.id,
          type: "Client",
          name: `${client?.first_name || ""} ${client?.last_name || ""} - Client`.trim(),
        });
      });
    }

    // Add defendants
    if (partiesData?.defendants) {
      partiesData?.defendants?.forEach((defendant) => {
        combinedList.push({
          id: defendant.id,
          type: "Defendant",
          name: `${
            defendant?.defendantType?.id === 1
              ? `${defendant?.first_name || ""} ${defendant?.last_name || ""}`.trim()
              : defendant?.entity_name || ""
          } - Defendant`,
        });
      });
    }

    // Add other parties
    if (partiesData?.other_parties) {
      partiesData?.other_parties?.forEach((party) => {
        combinedList.push({
          id: party.id,
          type: "OtherParty",
          name: `${party?.party_first_name || party?.first_name || ""} ${party?.party_last_name || party?.last_name || ""} - OtherParty`.trim(),
        });
      });
    }
    return combinedList;
  };

  const getFilteredFromOptions = () => {
    if (!partiesData) return [];

    // If no "to" is selected, return all options
    if (!form.server) return partiesData;

    // Filter out the selected "to" option from "from" options
    return partiesData.filter((party) => party.id !== form.server.id);
  };

  // Update the handleChange function to store entire objects and clear "from" when "to" changes
  const handleToFromChange = (e) => {
    const { name, value } = e.target;

    if (name === "server") {
      // Find the selected party object
      const selectedParty = partiesData?.find((party) => party.id === parseInt(value)) || null;

      // If changing "to" and the new value equals current "from" ID, clear "from"
      if (value && form.served && parseInt(value) === form.served.id) {
        setForm((prevForm) => ({
          ...prevForm,
          server: selectedParty,
          served: null, // Clear from selection
        }));
      } else {
        setForm((prevForm) => ({
          ...prevForm,
          server: selectedParty,
        }));
      }
    } else if (name === "served") {
      // Find the selected party object
      const selectedParty = partiesData?.find((party) => party.id === parseInt(value)) || null;

      setForm((prevForm) => ({
        ...prevForm,
        served: selectedParty,
      }));
    }
  };

  const [meetingPlace, setMeetingPlace] = useState("zoom");

  return (
    <>
      <Modal
        show={showPopup}
        onHide={handleClose}
        centered
        dialogClassName="max-width-860px"
        style={{
          opacity: stateShow ? "0.5" : "1",
        }}
      >
        <Modal.Header
          style={{
            background: "red",
          }}
          className="text-center height-25 p-0 d-flex align-items-center justify-content-center"
        >
          <h4
            style={{
              fontSize: "14px",
            }}
            className="text-white height-25 d-flex align-items-center justify-content-center"
          >
            Add Statute Of Limitations
          </h4>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ minHeight: "510px", maxHeight: "570px" }}>
          <div className="font-weight-600">
            <span className="d-flex justify-content-center">
              {litigationDetail?.court_name ? (
                litigationDetail.court_name
              ) : (
                <span className="text-grey">Court</span>
              )}
              <span
                className=""
                style={{
                  color: "var(--primary-25)",
                }}
              >
                {"\u00A0|\u00A0"}
              </span>
              {litigationDetail?.case_number ? (
                <span>Case #{litigationDetail?.case_number}</span>
              ) : (
                <span className="text-grey">Case #</span>
              )}
            </span>
            <span className="d-flex justify-content-center">
              {litigationDetail?.jurisdiction_obj?.name ? (
                litigationDetail.jurisdiction_obj.name
              ) : (
                <span className="text-grey">Jurisdiction</span>
              )}
              <span
                className=""
                style={{
                  color: "var(--primary-25)",
                }}
              >
                {"\u00A0|\u00A0"}
              </span>
              {litigationDetail?.jurisdiction_type?.name ? (
                <span className="mx-1">{litigationDetail.jurisdiction_type.name} Court</span>
              ) : (
                <span className="text-grey">Court Type</span>
              )}
              <span
                className=""
                style={{
                  color: "var(--primary-25)",
                }}
              >
                {"\u00A0|\u00A0"}
              </span>
              {litigationDetail?.state?.name ? (
                litigationDetail.state.name
              ) : (
                <span className="text-grey">State</span>
              )}
              <span
                className=""
                style={{
                  color: "var(--primary-25)",
                }}
              >
                {"\u00A0|\u00A0"}
              </span>
              {litigationDetail?.county?.name ? (
                <span className="ms-1">{litigationDetail.county.name} County</span>
              ) : (
                <span className="text-grey">County</span>
              )}
            </span>
          </div>

          <SOLWizardTabs
            selectedId={activeTab}
            onSelect={setActiveTab}
            hasEvent={Boolean(form?.event_id)}
            showLocation={showLocationTab}
          />

          {activeTab === "eventTemplate" &&
            (!litigationDetail?.id ? (
              <div
                className="text-center font-weight-600 text-primary p-l-5 p-r-5 w-100 m-t-5"
                style={{ fontSize: "15px" }}
              >
                No Litigation exists for this Case. Create a Litigation first from{" "}
                <i className="ic ic-17 ic-litigation" /> Litigation Page.
              </div>
            ) : eventsData?.length > 0 ? (
              <div className="w-100 m-t-5 p-l-5 p-r-5">
                <div
                  className="invisible-scrollbar overflow-scroll"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(279px, 1fr))",
                    // maxWidth: "calc(3 * 1fr)",
                    alignContent: "start",
                    gap: "5px",
                    height: "125px",
                  }}
                >
                  {eventsData?.map((item) => {
                    const isSelected = form?.event_id === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`event-type-button2 btn-hover-litigation-act height-21 ${isSelected ? "selected" : "unselected"}`}
                        onClick={() => {
                          handleEventOptionSelect(item.id);
                        }}
                      >
                        <>
                          <img
                            src={SOLIcon}
                            className="d-flex align-items-center justify-content-center m-r-5 ic-15"
                          />
                        </>
                        <span
                          className="text-nowrap"
                          style={{
                            textOverflow: "ellipsis",
                            maxWidth: "233px",
                            overflow: "hidden",
                          }}
                        >
                          {item.event_name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : fetchingSOLEvents ? (
              <div className="w-100 m-t-5 d-flex justify-content-center align-items-center">
                <PulseLoader
                  loading={true}
                  size={14}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  color="red"
                  style={{
                    paddingTop: "5px",
                  }}
                />
              </div>
            ) : (
              <div
                className="text-center font-weight-600 text-primary p-l-5 p-r-5 w-100 m-t-5"
                style={{ fontSize: "15px" }}
              >
                No Matching Statute Of Limitation Template. Create Templates from{" "}
                <i className="ic ic-17 ic-directory" />
                Directory Page first.
              </div>
            ))}

          {activeTab === "parties" && form?.event_id && (
            <div className="m-t-5 p-l-5 p-r-5">
              {clientsList?.length > 0 && (
                <div className="row mx-auto ps-0 pe-0 align-items-start m-b-5">
                  <div className="col-2 p-l-5 p-r-5 text-start">
                    <span className="d-inline-block text-grey">
                      <nobr>Client</nobr>
                    </span>
                  </div>
                  <div className="col-10 p-l-5 p-r-5">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(279px, 1fr))",
                        gap: "5px",
                      }}
                    >
                      {clientsList.map((client) => {
                        const isSelected = selectedClientId === client.id;
                        return (
                          <button
                            key={client.id}
                            type="button"
                            className={`event-type-button2 btn-hover-litigation-act height-21 ${isSelected ? "selected" : "unselected"}`}
                            onClick={() => handleClientToggle(client.id)}
                          >
                            <i className="ic ic-15 ic-client m-r-5" />
                            <span
                              className="text-nowrap"
                              style={{
                                textOverflow: "ellipsis",
                                maxWidth: "340px",
                                overflow: "hidden",
                              }}
                            >
                              {`${client?.first_name || ""} ${client?.last_name || ""}`.trim()}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              {selectedEvent?.defendant_types?.length > 0 && defendantsList?.length > 0 && (
                <div className="row mx-auto ps-0 pe-0 align-items-start m-b-5 m-t-20">
                  <div className="col-2 p-l-5 p-r-5 text-start">
                    <span className="d-inline-block text-grey">
                      <nobr>Defendant</nobr>
                    </span>
                  </div>
                  <div className="col-10 p-l-5 p-r-5">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(279px, 1fr))",
                        gap: "5px",
                      }}
                    >
                      {defendantsList.map((defendant) => {
                        const isSelected = selectedDefendantId === defendant.id;
                        return (
                          <button
                            key={defendant.id}
                            type="button"
                            className={`event-type-button2 btn-hover-litigation-act height-21 ${isSelected ? "selected" : "unselected"}`}
                            onClick={() => handleDefendantToggle(defendant.id)}
                          >
                            <i className="ic ic-15 ic-defendants m-r-5" />
                            <span
                              className="text-nowrap"
                              style={{
                                textOverflow: "ellipsis",
                                maxWidth: "340px",
                                overflow: "hidden",
                              }}
                            >
                              {getDefendantDisplayName(defendant)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "information" && form?.event_id && (
            <>
              <div
                style={{
                  background: "red",
                }}
                className="text-center height-21 d-flex align-items-center p-0 m-t-5"
              >
                <h5 className="text-white mx-auto">
                  Calculated Dates, Deadlines and Additional Events
                </h5>
              </div>
              <div className="m-t-5 p-l-5 p-r-5">
                {showStartDate && (
                  <div className="row mx-auto ps-0 pe-0 align-items-center m-b-5">
                    <div className="col-2 p-l-5 p-r-5 text-start">
                      <span className="d-inline-block text-grey">
                        <nobr>{startDateLabel}</nobr>
                      </span>
                    </div>
                    <div className="col-10 ps-0 pe-0">
                      <div
                        className={form?.is_allday ? "col-12 p-l-5 pe-0" : "row mx-auto ps-0 pe-0"}
                      >
                        <span className={form?.is_allday ? "w-100" : "col-6 p-l-5 p-r-5"}>
                          <input
                            type="date"
                            placeholder="DD-MM-YYYY"
                            value={form?.start_date}
                            className="form-control height-21 p-l-5 p-0 p-r-5"
                            name="start_date"
                            onChange={handleChange}
                          />
                        </span>
                        {(form?.sameDay || form?.is_duration) && (
                          <span className="col-6 p-l-5 p-r-0">
                            <input
                              type="time"
                              placeholder="THH:MM"
                              value={form?.start_time}
                              className="form-control height-21 p-l-5 p-0 p-r-5"
                              name="start_time"
                              onChange={handleChange}
                            />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {showEndDate && (
                  <div className="row mx-auto ps-0 pe-0 align-items-center m-b-5">
                    <div className="col-2 p-l-5 p-r-5 text-start">
                      <span className="d-inline-block text-grey">
                        <nobr>{endDateLabel}</nobr>
                      </span>
                    </div>
                    <div className="col-10 ps-0 pe-0">
                      <div
                        className={form?.is_allday ? "col-12 p-l-5 pe-0" : "row mx-auto ps-0 pe-0"}
                      >
                        <span className={form?.is_allday ? "w-100" : "col-6 p-l-5 p-r-5"}>
                          <input
                            type="date"
                            placeholder="DD-MM-YYYY"
                            value={form?.end_date}
                            className="form-control height-21 p-l-5 p-0 p-r-5"
                            name="end_date"
                            onChange={handleChange}
                          />
                          {calculatingEndDate && (
                            <div
                              className="spinner-border spinner-border-sm text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          )}
                        </span>
                        {(form?.sameDay || form?.is_duration) && (
                          <span className="col-6 p-l-5 p-r-0">
                            <input
                              type="time"
                              placeholder="THH:MM"
                              value={form?.end_time}
                              className="form-control height-21 p-l-5 p-0 p-r-5"
                              name="end_time"
                              onChange={handleChange}
                            />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {showServiceDateField && (
                  <div className="row mx-auto ps-0 pe-0 align-items-center m-b-5">
                    <div className="col-2 p-l-5 p-r-5 text-start">
                      <span className="d-inline-block text-grey">Service Method</span>
                    </div>
                    <div className="col-10 p-l-5 p-r-0">
                      <select
                        name="for_service"
                        className="form-select form-control height-21 py-0 p-l-5 p-r-5"
                        value={form.for_service}
                        onChange={(e) => {
                          handleIntChange(e);
                          setServiceMethodId(e.target.value);
                        }}
                      >
                        <option value="">Select Service Method</option>
                        {serviceData?.map((item) => (
                          <option key={item?.id} value={item?.id}>
                            {item?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {showToFrom && (
                  <div className="row align-items-center">
                    <div className="col-3 text-start text-grey">
                      To {toLabel}/From {fromLabel}
                    </div>
                    <div className="col-9">
                      <div className="row">
                        <div className="col-6 p-r-5">
                          <select
                            name="server"
                            className="form-select form-control height-21 py-0"
                            value={form.server?.id || ""}
                            onChange={handleToFromChange}
                          >
                            <option value="">Select {fromLabel}</option>
                            {partiesData?.map((obj) => (
                              <option key={obj.id} value={obj.id}>
                                {obj.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6 p-l-5">
                          <select
                            name="served"
                            className="form-select form-control height-21 py-0"
                            value={form.served?.id || ""}
                            onChange={handleToFromChange}
                          >
                            <option value="">Select {toLabel}</option>
                            {getFilteredFromOptions()?.map((obj) => (
                              <option key={obj.id} value={obj.id}>
                                {obj.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {calculatedDates?.length > 0 && (
                  <>
                    <div style={{ height: "auto" }}>
                      <div
                        className="invisible-scrollbar"
                        style={{
                          maxHeight: "100px",
                          overflowY: "scroll",
                          padding: "5px",
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          gap: "5px",
                          height: "auto",
                        }}
                      >
                        {calculatedDates.map((date, index) => {
                          const calculatedDate = formatCourtDateTime(
                            calculatedDatePreviews[`calculated-date-${date.id}`]
                          );
                          return (
                            <div
                              key={index}
                              className="form-check d-flex align-items-center height-21"
                            >
                              <label className="form-check-label d-flex align-items-center">
                                <input
                                  id="calculatedDate-input"
                                  type="checkbox"
                                  className="form-check-input cursor-pointer"
                                  value={date.id}
                                  style={{ accentColor: "grey" }}
                                  onChange={handleCalDatesChange}
                                  checked={form.calculated_dates?.includes(date.id)}
                                />
                                {date?.calculated_date_name}
                                {" - "}
                                {calculatedDate
                                  ? `${calculatedDate}`
                                  : date?.dependant_date?.dependent_date_name
                                    ? `${date?.dependant_date?.dependent_date_name}`
                                    : ""}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          {activeTab === "workers" && form?.event_id !== null && (
            <div className="w-100 m-t-5">
              <AssignNewActCaseWorkersArea
                panelLabel="CASE WORKERS ASSIGNED TO ACT"
                case_users={firmUsers?.slice(0, 3)}
                case_users1={firmUsers?.slice(3, 6)}
                otherUser={otherFirmUser}
                uniqueIdUsers={uniqueIdUsers}
                assignedUsers={assignedUsers}
                setAssignedUsers={setAssignedUsers}
                setUniqueIdUsers={setUniqueIdUsers}
              />
            </div>
          )}

          {activeTab === "location" && showLocationTab && (
            <>
              <div
                style={{
                  background: "red",
                }}
                className="text-center height-21 d-flex align-items-center p-0 m-t-5"
              >
                <h5 className="text-white mx-auto">Location</h5>
              </div>
              <div className="m-t-5 p-l-5 p-r-5">
                <div className="d-flex w-100 p-b-5 ps-0">
                  <div className="form-check ps-0 p-r-5 col-4">
                    <button
                      type="button"
                      className={`event-type-button2 w-100 height-21 ${form.locationType === 1 ? "selected" : "unselected"}`}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          locationType: 1,
                        }))
                      }
                    >
                      <span className="text-nowrap">Select Location</span>
                    </button>
                  </div>

                  <div className="form-check ps-0 pe-0 col-4">
                    <button
                      type="button"
                      className={`event-type-button2 w-100 height-21 ${form.locationType === 2 ? "selected" : "unselected"}`}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          locationType: 2,
                        }))
                      }
                    >
                      <span className="text-nowrap">Input Location</span>
                    </button>
                  </div>

                  <div className="form-check p-l-5 p-r-0 col-4">
                    <button
                      type="button"
                      className={`event-type-button2 w-100 height-21 ${form.locationType === 3 ? "selected" : "unselected"}`}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          locationType: 3,
                        }))
                      }
                    >
                      <span className="text-nowrap">Input Meeting Link</span>
                    </button>
                  </div>
                </div>
                {form?.locationType === 1 && (
                  <div>
                    <div className="dropdown-container" ref={dropdownRef2}>
                      <div
                        className="form-select form-control d-flex height-21 py-0 m-b-5 overflow-hidden"
                        onClick={handleDropdownToggle2}
                      >
                        {selectedIndex !== "" && caseAddresses[selectedIndex] ? (
                          <>
                            <i
                              className={`ic ic-19 ${caseAddresses[selectedIndex]?.contact?.type === "Defendant" ? "ic-defendants" : "ic-client"} m-l-5 m-r-5`}
                            />{" "}
                            {caseAddresses[selectedIndex]?.label} |{" "}
                            {caseAddresses[selectedIndex]?.contact?.[
                              caseAddresses[selectedIndex]?.contact?.selectedContact
                            ]?.address1 &&
                              `${
                                caseAddresses[selectedIndex]?.contact?.[
                                  caseAddresses[selectedIndex]?.contact?.selectedContact
                                ]?.address1
                              }, `}
                            {caseAddresses[selectedIndex]?.contact?.[
                              caseAddresses[selectedIndex]?.contact?.selectedContact
                            ]?.address2 &&
                              `${
                                caseAddresses[selectedIndex]?.contact?.[
                                  caseAddresses[selectedIndex]?.contact?.selectedContact
                                ]?.address2
                              }, `}
                            {caseAddresses[selectedIndex]?.contact?.[
                              caseAddresses[selectedIndex]?.contact?.selectedContact
                            ]?.city &&
                              `${
                                caseAddresses[selectedIndex]?.contact?.[
                                  caseAddresses[selectedIndex]?.contact?.selectedContact
                                ]?.city
                              }, `}
                            {caseAddresses[selectedIndex]?.contact?.[
                              caseAddresses[selectedIndex]?.contact?.selectedContact
                            ]?.state &&
                              `${
                                caseAddresses[selectedIndex]?.contact?.[
                                  caseAddresses[selectedIndex]?.contact?.selectedContact
                                ]?.state
                              } `}
                            {caseAddresses[selectedIndex]?.contact?.[
                              caseAddresses[selectedIndex]?.contact?.selectedContact
                            ]?.zip &&
                              `${
                                caseAddresses[selectedIndex]?.contact?.[
                                  caseAddresses[selectedIndex]?.contact?.selectedContact
                                ]?.zip
                              }`}
                          </>
                        ) : (
                          <>
                            <span>Select Litigation Act Location</span>
                          </>
                        )}
                      </div>
                      {isOpen2 && (
                        <ul
                          className="dropdown-list overflow-scroll"
                          style={{ maxHeight: "250px" }}
                        >
                          <li onClick={() => handleAddressChange("")}>
                            <i className={"ic ic-19 m-r-5"} />{" "}
                            <span>Select Litigation Act Location</span>
                          </li>
                          {caseAddresses.map((option, index) => (
                            <li key={index} onClick={() => handleAddressChange(index)}>
                              {/* Show custom icon if src exists */}
                              {option.src ? (
                                <img
                                  src={option.src}
                                  alt=""
                                  className="m-r-5"
                                  style={{ width: "19px", height: "19px" }}
                                />
                              ) : (
                                /* Default className-based icons for Defendant and Client */
                                <i
                                  className={`ic ic-19 ${
                                    option?.contact?.type === "Defendant"
                                      ? "ic-defendants"
                                      : option?.contact?.type === "Client"
                                        ? "ic-client"
                                        : ""
                                  } m-r-5`}
                                />
                              )}{" "}
                              {option?.label} |{" "}
                              {option?.contact?.[option?.contact?.selectedContact]?.address1 &&
                                `${option?.contact?.[option?.contact?.selectedContact]?.address1}, `}
                              {option?.contact?.[option?.contact?.selectedContact]?.address2 &&
                                `${option?.contact?.[option?.contact?.selectedContact]?.address2}, `}
                              {option?.contact?.[option?.contact?.selectedContact]?.city &&
                                `${option?.contact?.[option?.contact?.selectedContact]?.city}, `}
                              {option?.contact?.[option?.contact?.selectedContact]?.state &&
                                `${option?.contact?.[option?.contact?.selectedContact]?.state}`}
                              {option?.contact?.[option?.contact?.selectedContact]?.zip &&
                                ` ${option?.contact?.[option?.contact?.selectedContact]?.zip}`}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
                {form?.locationType === 2 && (
                  <>
                    <div className="row align-items-center m-b-5">
                      <div className="col-12">
                        <input
                          type="text"
                          placeholder="Name"
                          value={form?.name}
                          className="form-control bg-white height-21"
                          onChange={handleChange}
                          name="name"
                        />
                      </div>
                    </div>
                    <div className="row align-items-center m-b-5">
                      <span className="col-6 p-r-5">
                        <input
                          type="text"
                          placeholder="Address 1"
                          value={form?.address1}
                          className="form-control bg-white height-21"
                          onChange={handleChange}
                          name="address1"
                        />
                      </span>
                      <span className="col-6 p-l-5">
                        <input
                          type="text"
                          placeholder="Address 2"
                          value={form?.address2}
                          className="form-control bg-white height-21"
                          onChange={handleChange}
                          name="address2"
                        />
                      </span>
                    </div>
                    <div className="row align-items-center m-b-5">
                      <span className="col-6 p-r-5">
                        <input
                          type="text"
                          placeholder="City"
                          value={form?.city}
                          className="form-control bg-white height-21"
                          onChange={handleChange}
                          name="city"
                        />
                      </span>
                      <span className="col-2 p-r-5 p-l-5">
                        <div className="d-flex-1 p-l-0 position-relative height-21 custom-select-new-provider">
                          <div
                            className="dropdown-button rounded-0 p-0 p-l-5 form-control height-21 d-flex align-items-center"
                            onClick={handleStateShow}
                          >
                            <span id="selectedOption">
                              {locationState ? (
                                <div className="d-flex align-items-center">
                                  <svg
                                    style={{
                                      width: "15px",
                                      height: "15px",
                                      fill: "var(--primary-80)",
                                      color: "var(--primary-80)",
                                      stroke: "var(--primary-80)",
                                    }}
                                    className={`icon icon-state-${locationState}`}
                                  >
                                    <use xlinkHref={`#icon-state-${locationState}`} />
                                  </svg>
                                  {locationState}
                                </div>
                              ) : (
                                "Select State"
                              )}
                            </span>
                          </div>
                        </div>
                      </span>
                      <span className="col-4 p-l-5">
                        <input
                          type="text"
                          placeholder="Zip"
                          value={form?.zip}
                          className="form-control bg-white height-21"
                          onChange={handleChange}
                          name="zip"
                        />
                      </span>
                    </div>
                    <div className="row align-items-center m-b-5">
                      <span className="col-6 p-r-5">
                        <input
                          type="text"
                          placeholder="Phone"
                          value={form?.phone_number}
                          className="form-control bg-white height-21"
                          onChange={handleChange}
                          name="phone_number"
                        />
                      </span>
                      <span className="col-6 p-l-5">
                        <input
                          type="text"
                          placeholder="Fax"
                          value={form?.fax}
                          className="form-control bg-white height-21"
                          onChange={handleChange}
                          name="fax"
                        />
                      </span>
                    </div>
                    <div className="row align-items-center m-b-5">
                      <span className="col-6 p-r-5">
                        <input
                          type="text"
                          placeholder="Website"
                          value={form?.website}
                          className="form-control bg-white height-21"
                          onChange={handleChange}
                          name="website"
                        />
                      </span>
                      <span className="col-6 p-l-5">
                        <input
                          type="text"
                          placeholder="Email"
                          value={form?.email}
                          className="form-control bg-white height-21"
                          onChange={handleChange}
                          name="email"
                        />
                      </span>
                    </div>
                  </>
                )}
              </div>
              {form?.locationType === 3 && (
                <>
                  <div className="row mx-auto m-b-5">
                    <div
                      className="form-check col-3 p-l-5 p-r-5"
                      style={{
                        gap: "5px",
                      }}
                      onClick={() => setMeetingPlace("zoom")}
                    >
                      <input
                        type="radio"
                        value="zoom"
                        checked={meetingPlace === "zoom"}
                        className="form-check cursor-pointer"
                        style={{ accentColor: "grey" }}
                      />
                      <label className="form-check-label">Zoom</label>
                    </div>

                    <div
                      className="form-check col-3 p-l-5 p-r-5"
                      style={{
                        gap: "5px",
                      }}
                      onClick={() => setMeetingPlace("teams")}
                    >
                      <input
                        type="radio"
                        value="teams"
                        checked={meetingPlace === "teams"}
                        className="form-check cursor-pointer"
                        style={{ accentColor: "grey" }}
                      />
                      <label className="form-check-label">Microsoft Teams</label>
                    </div>

                    <div
                      className="form-check col-3 p-l-5 p-r-5"
                      style={{
                        gap: "5px",
                      }}
                      onClick={() => setMeetingPlace("meet")}
                    >
                      <input
                        type="radio"
                        value="meet"
                        checked={meetingPlace === "meet"}
                        // onChange={() => setMeetingPlace("meet")}
                        className="form-check cursor-pointer"
                        style={{ accentColor: "grey" }}
                      />
                      <label className="form-check-label">Google Meet</label>
                    </div>

                    <div
                      className="form-check col-3 p-l-5 p-r-5"
                      style={{
                        gap: "5px",
                      }}
                      onClick={() => setMeetingPlace("other")}
                    >
                      <input
                        type="radio"
                        value="other"
                        checked={meetingPlace === "other"}
                        className="form-check cursor-pointer"
                        style={{ accentColor: "grey" }}
                      />
                      <label className="form-check-label">Other</label>
                    </div>
                  </div>
                  <div className="row mx-auto m-b-5">
                    <span className="col-12 p-l-5 p-r-5">
                      <textarea
                        type="url"
                        placeholder="meetingurl"
                        value={form?.meetingurl}
                        className="form-control bg-white p-l-5 p-0 p-r-5"
                        style={{
                          height: "85px",
                          resize: "none",
                        }}
                        onChange={handleChange}
                        name="meetingurl"
                      />
                    </span>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <div className="d-flex justify-content-between p-r-5 p-b-5 p-l-5">
          <Button
            type="button"
            className="height-25"
            style={{ padding: "0px 12px" }}
            onClick={handleClose}
          >
            Close
          </Button>
          <div>
            <Button
              type="submit"
              variant="success"
              onClick={handleSubmit}
              className="height-25"
              style={{ padding: "0px 12px" }}
              disabled={shouldDisableSubmit()}
            >
              Save and Close
            </Button>
          </div>
        </div>
      </Modal>
      {stateShow && (
        <SelectStateModal
          show={stateShow}
          handleClose={handleStateShow}
          onChange={handleStateChange}
          statesData={statesData}
        />
      )}
    </>
  );
}
const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.showPopup === nextProps.showPopup &&
    prevProps.litigationDetail === nextProps.litigationDetail &&
    prevProps.handleClose === nextProps.handleClose
  );
};

export default React.memo(SOLActPopupCasePage, areEqual);
